import BlockRenderer from '@root/joinroot.com/src/components/blocks/block-renderer';
import Layout from '@root/joinroot.com/src/components/layout';
import MixpanelDetails from '@root/joinroot.com/src/components/mixpanel-details';
import PropTypes from '@root/vendor/prop-types';
import Seo from '@root/joinroot.com/src/components/seo';
import buildPathFromSlug from '@root/joinroot.com/src/utils/build-path-from-slug';
import useAnalytics from '@root/core/src/hooks/use-analytics';
import useSaveAttribution from '@root/attribution/src/hooks/use-save-attribution';
import { graphql } from 'gatsby';

export default function BasicPage({ data, location }) {
  const basicPage = data.contentfulBasicPage;
  const analyticsContext = `BASIC_PAGE_${basicPage.id}`;

  useAnalytics(analyticsContext);
  useSaveAttribution(location);

  return (
    <Layout
      ctaLink={basicPage.ctaLink}
      ctaText={basicPage.ctaText}
      includeHotjar={basicPage.publishingMetadata.includeHotjar}
      includeOptimizely={basicPage.publishingMetadata.includeOptimizely}
      isCtaButtonDisabled={basicPage.disableCtaButton}
      isStickyFooterDisabled={basicPage.disableStickyFooter}
      location={location}
    >
      <Seo
        canonical={`https://www.joinroot.com${buildPathFromSlug(basicPage.publishingMetadata.slug)}`}
        deIndexPage={basicPage.publishingMetadata.deIndexPage}
        description={basicPage.publishingMetadata.pageDescription}
        image={basicPage.publishingMetadata.pageThumbnail?.file?.url}
        title={basicPage.publishingMetadata.pageTitle}
      />

      <MixpanelDetails
        analyticsContext={analyticsContext}
        isPageView={true}
      />

      {basicPage.blocks && basicPage.blocks.map((block) => (
        <BlockRenderer
          block={block}
          key={block.id}
        />
      ))}
    </Layout>
  );
}

BasicPage.propTypes = {
  data: PropTypes.shape({
    contentfulBasicPage: PropTypes.shape({
      id: PropTypes.string.isRequired,
      publishingMetadata: PropTypes.shape({
        deIndexPage: PropTypes.bool,
        includeHotjar: PropTypes.bool,
        includeOptimizely: PropTypes.bool,
        pageTitle: PropTypes.string.isRequired,
        pageDescription: PropTypes.string.isRequired,
        pageThumbnail: PropTypes.shape({
          file: PropTypes.shape({
            url: PropTypes.string,
          }),
        }),
        slug: PropTypes.string.isRequired,
      }).isRequired,

      ctaLink: PropTypes.string,
      ctaText: PropTypes.string,
      disableCtaButton: PropTypes.bool,
      disableStickyFooter: PropTypes.bool,

      blocks: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          content: PropTypes.shape({
            json: PropTypes.object.isRequired,
          }),
          sys: PropTypes.shape({
            contentType: PropTypes.shape({
              sys: PropTypes.shape({
                id: PropTypes.string.isRequired,
              }),
            }),
          }),
        }).isRequired,
      ),
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
    pathname: PropTypes.string.isRequired,
  }.isRequired),
};

export const query = graphql`
query ($id: String!) {
  contentfulBasicPage(id: {eq: $id}) {
    id

    publishingMetadata {
      ...ContentfulPublishingMetadata_fragment
    }

    ctaText
    ctaLink
    disableStickyFooter
    disableCtaButton

    blocks {
      ...ContentfulAvailabilityMap_fragment
      ...ContentfulCustomReactComponent_fragment
      ...ContentfulDisclaimerSection_fragment
      ...ContentfulExpandableSection_fragment
      ...ContentfulFaqSection_fragment
      ...ContentfulHero_fragment
      ...ContentfulHeroSimple_fragment
      ...ContentfulIconToutGrid_fragment
      ...ContentfulIconToutSection_fragment
      ...ContentfulIconToutSideBySide_fragment
      ...ContentfulImageToutSection_fragment
      ...ContentfulImageToutSideBySideSection_fragment
      ...ContentfulPressSection_fragment
      ...ContentfulQuoteToutSection_fragment
      ...ContentfulRichTextSection_fragment
      ...ContentfulSpacer_fragment
      ...ContentfulVariationContainer_fragment
      ...ContentfulVideoToutSection_fragment
    }
  }
}
`;
